import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { ROUTES } from 'configs';
import {
  MainMenuItemWrapper,
  MainMenuItem,
  DefaultCommunityImage,
  PendingSettingsInvite,
  Tooltip,
} from 'components';
import { INFO_POPUP_V2 } from 'constants/popups';
import { openModal, closeModal } from 'store/actionCreators/modal';
import { CommunityRoles } from 'types';
import type { MainMenuItemDisableReason } from '../MainMenuItemWrapper';

import * as S from './styled';

export type MainMenuCommunityDisableReason = MainMenuItemDisableReason;

interface MainMenuCommunityItemProps {
  id: string;
  index?: number;
  isShowTooltip: boolean;
  title: string;
  image: string | null;
  disableReason?: MainMenuCommunityDisableReason;
  invitesCount: number;
  userRole: CommunityRoles;
}

function PauseGlyph() {
  return (
    <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden>
      <rect x="0.5" y="1" width="2.2" height="6" rx="0.5" fill="currentColor" />
      <rect x="5.3" y="1" width="2.2" height="6" rx="0.5" fill="currentColor" />
    </svg>
  );
}

function LockGlyph() {
  return (
    <svg width="9" height="9" viewBox="0 0 12 12" aria-hidden>
      <path
        fill="currentColor"
        d="M6 1.25A2.25 2.25 0 018.25 3.5V4.5h.5A1.25 1.25 0 0110 5.75v4.5A1.25 1.25 0 018.75 11.5h-5.5A1.25 1.25 0 012 10.25v-4.5A1.25 1.25 0 013.25 4.5H4V3.5A2.25 2.25 0 016 1.25zm0 1a1.25 1.25 0 00-1.25 1.25V4.5h2.5V3.5A1.25 1.25 0 006 2.25z"
      />
    </svg>
  );
}

const MainMenuCommunityItem: FC<MainMenuCommunityItemProps> = ({
  isShowTooltip,
  title,
  image,
  id,
  disableReason: disableReasonProp = 'none',
  invitesCount,
  userRole,
  index,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { communityId } = useParams();
  const isSelected = communityId === id;
  const marginTop = index === 0 && invitesCount > 0;

  const disableReason = disableReasonProp;
  const isDisabled = disableReason !== 'none';

  const sidebarAriaHint =
    disableReason === 'blocked'
      ? t('community.sidebarCommunityBlockedHint')
      : disableReason === 'subscriptionSlotInactive'
      ? t('community.sidebarCommunityPausedHint')
      : '';

  const linkAriaLabel =
    isDisabled && sidebarAriaHint ? `${title}. ${sidebarAriaHint}` : title;

  const onDisabledClick = () => {
    const isBlocked = disableReason === 'blocked';
    dispatch(
      openModal({
        type: INFO_POPUP_V2,
        data: {
          headerText: isBlocked
            ? t('community.userBlockedTitle')
            : t('community.subscriptionSlotInactiveTitle'),
          messageText: isBlocked
            ? t('community.userBlockedText')
            : t('community.subscriptionSlotInactiveText'),
          defaultButtonText: 'Ok',
          type: 'warning',
          withoutCloseButton: true,
          onConfirm: () => {
            dispatch(closeModal());
          },
        },
      }),
    );
  };

  return (
    <Tooltip
      isVisible={isShowTooltip}
      text={
        isDisabled && sidebarAriaHint ? `${title} - ${sidebarAriaHint}` : title
      }
      position="right-center"
    >
      <div
        style={marginTop ? { marginTop: '8px' } : {}}
        onClick={isDisabled ? onDisabledClick : () => {}}
      >
        <MainMenuItemWrapper
          to={`${ROUTES.COMMUNITY}/${id}/overall`}
          isDisabled={isDisabled}
          isSelected={isSelected && !isDisabled}
          disableReason={disableReason}
          aria-label={linkAriaLabel}
        >
          {invitesCount > 0 &&
            (userRole === CommunityRoles.ADMIN ||
              userRole === CommunityRoles.OWNER) && (
              <PendingSettingsInvite
                invitesCount={invitesCount}
                addCss={{ top: -2, right: 0 }}
              />
            )}
          <S.AvatarStack $dimmed={isDisabled}>
            {image ? (
              <MainMenuItem image={image} />
            ) : (
              <DefaultCommunityImage title={title.charAt(0).toUpperCase()} />
            )}
          </S.AvatarStack>
          {isDisabled ? (
            <S.StateBadge
              $variant={disableReason}
              aria-hidden
              title={
                disableReason === 'blocked'
                  ? t('community.sidebarCommunityBlockedHint')
                  : t('community.sidebarCommunityPausedHint')
              }
            >
              {disableReason === 'blocked' ? <LockGlyph /> : <PauseGlyph />}
            </S.StateBadge>
          ) : null}
        </MainMenuItemWrapper>
      </div>
    </Tooltip>
  );
};

export default MainMenuCommunityItem;
